import type { ReactNode } from 'react';

const CONTACT_EMAIL = '19lottd@gmail.com';

interface PolicySection {
  title: string;
  body: ReactNode;
}

const SECTIONS: PolicySection[] = [
  {
    title: 'What this site is',
    body: (
      <p>
        dalton-lott.com is the personal website of Dalton Lott. Parts of the site (for example the
        integrations pages) publicly showcase Dalton&apos;s own fitness and connected-service data.
        Data belonging to anyone else is never displayed publicly.
      </p>
    ),
  },
  {
    title: 'Data collected',
    body: (
      <>
        <p>
          <strong>Signing in with Google.</strong> If you sign in, the site receives your name,
          email address, and profile picture from the Google account you choose. A session cookie
          keeps you signed in.
        </p>
        <p>
          <strong>Connecting WHOOP.</strong> If you explicitly connect a WHOOP account, the site
          imports your WHOOP profile (name and WHOOP user ID), body measurements (height, weight,
          max heart rate), and your cycle (strain), recovery (including HRV and resting heart rate),
          sleep, and workout records via the official WHOOP API. OAuth tokens are stored so the data
          can stay in sync until you disconnect.
        </p>
        <p>
          <strong>Browsing without an account.</strong> A random anonymous identifier and page
          preferences are kept in your browser&apos;s local storage. Standard server logs (such as
          IP addresses and requests) are retained for security and debugging.
        </p>
      </>
    ),
  },
  {
    title: 'How data is used',
    body: (
      <p>
        Data is used only to operate the site: showing your own information back to you, keeping
        connected services in sync, and securing the service. There is no advertising, no
        third-party analytics or trackers, and your data is never sold or shared for marketing.
        WHOOP data is only ever read from WHOOP — nothing is written back to your WHOOP account.
      </p>
    ),
  },
  {
    title: 'Storage and sharing',
    body: (
      <p>
        Data is stored on Amazon Web Services in the United States. Databases are encrypted at rest
        and traffic uses HTTPS. The only third parties involved are infrastructure providers (AWS)
        and the services you explicitly connect (Google for sign-in, WHOOP via its API).
      </p>
    ),
  },
  {
    title: 'Your choices and deletion',
    body: (
      <>
        <p>
          You can disconnect WHOOP at any time from Settings. Disconnecting revokes the site&apos;s
          access with WHOOP and stops all syncing; previously imported history remains in your
          account until you ask for it to be deleted.
        </p>
        <p>
          To permanently delete your account and all associated data — including imported WHOOP
          history — email{' '}
          <a className="underline underline-offset-2" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: 'Cookies',
    body: (
      <p>
        The site sets a session cookie when you sign in and a short-lived (10 minute) state cookie
        during the WHOOP connect flow to protect against cross-site request forgery. Local storage
        holds the anonymous guest identifier and display preferences. There are no advertising or
        tracking cookies.
      </p>
    ),
  },
  {
    title: 'Changes and contact',
    body: (
      <p>
        Updates to this policy are posted on this page with a new effective date. Questions or
        requests:{' '}
        <a className="underline underline-offset-2" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>
        .
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    // Mirror settings.tsx: the global shell (ww-root) is dark and sets light
    // text; re-assert the light theme's text color for this light page.
    <main className="flex-1 bg-gradient-to-b from-background to-muted text-foreground">
      <div className="mx-auto w-full max-w-2xl px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Privacy Policy</h1>
          <p className="mt-1 text-sm text-muted-foreground">Effective July 3, 2026</p>
        </div>
        <div className="space-y-8">
          {SECTIONS.map(({ title, body }) => (
            <section key={title}>
              <h2 className="mb-2 text-lg font-semibold tracking-tight">{title}</h2>
              <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{body}</div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
